/**
 * AssetRadar — providers and the route table.
 *
 * Provider order is load-bearing:
 *   RouterProvider > AuthProvider > ThemeProvider > ToastProvider
 *     - the router is outermost because the guards (`RequireAuth`,
 *       `RequirePermission`) and `LoginPage` use `useLocation` / `<Navigate>`;
 *     - `AuthProvider` sits above the toasts so a 401 arriving from anywhere can
 *       drop the user while a toast is still on screen;
 *     - `ThemeProvider` sits between the two: it needs nothing from either, but
 *       everything below it (the shell's day/night switch, the login screen)
 *       may call `useTheme()`;
 *     - `ToastProvider` is innermost, so every page below it may call
 *       `useToast()` and its `.toast-stack` overlays the whole app.
 *
 * Route table (the permission strings are the frozen vocabulary of
 * app/auth/permissions.py — a typo here silently hides or exposes a page):
 *
 *   /login                public
 *   /                     RequireAuth > Layout > DashboardPage
 *   /cra-check            + dashboard.read
 *   /schwachstellen       + dt.read
 *   /triage               + triage.read
 *   /audit-log            + triage.read
 *   /verantwortlichkeit   + cmdb.read (edits additionally need cmdb.write)
 *   /stammdaten           + cmdb.read
 *   /beziehungen          + cmdb.read
 *   /import               + import.write
 *   /admin/users          + user.admin
 *   /admin/system/*       + system.admin; thematic child routes
 *   *                     NotFoundPage (inside the shell — the user stays logged
 *                         in and keeps the navigation)
 *
 * `RequireAuth` wraps the LAYOUT route, not each page: authentication is checked
 * once, and `RequirePermission` only ever runs for a user that already exists.
 * The two guards compose instead of duplicating the redirect logic.
 *
 * Deep links survive a hard reload because FastAPI's catch-all serves
 * index.html for unknown top-level paths (see app/routes/spa.py).
 * The browser data router uses real URLs and enables unsaved-work navigation
 * guards. The existing providers and route table remain inside its root route.
 */

import { createBrowserRouter, Route, RouterProvider, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';

import Layout from './components/Layout';
import RequireAuth from './components/RequireAuth';
import RequirePermission from './components/RequirePermission';
import { ToastProvider } from './components/Toast';
import { AuthProvider } from './lib/auth';
import { ThemeProvider } from './lib/theme';
import AdminUsersPage from './pages/AdminUsersPage';
import AuditLogPage from './pages/AuditLogPage';
import BeziehungenPage from './pages/BeziehungenPage';
import CraCheckPage from './pages/CraCheckPage';
import DashboardPage from './pages/DashboardPage';
import ImportPage from './pages/ImportPage';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import SchwachstellenPage from './pages/SchwachstellenPage';
import StammdatenPage from './pages/StammdatenPage';
import SystemPage from './pages/SystemPage';
import TriagePage from './pages/TriagePage';
import VerantwortlichkeitPage from './pages/VerantwortlichkeitPage';
import SystemCsafPage from './pages/system/SystemCsafPage';
import SystemDataModelPage from './pages/system/SystemDataModelPage';
import SystemOverviewPage from './pages/system/SystemOverviewPage';
import SystemSbomPage from './pages/system/SystemSbomPage';

function ApplicationRoutes(): ReactNode {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/*
              Pathless layout route: the children below are relative to '/', and
              everything inside is behind the session check.
            */}
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              {/*
                The landing page is guarded too. `GET /api/dashboard/overview`
                enforces `dashboard.read`, so an account without it (a user
                created with no role at all — the admin UI permits that) would
                otherwise land on a page whose only content is a German 403
                blaming CMDBuild. The guard states the real reason instead.
              */}
              <Route
                index
                element={
                  <RequirePermission permission="dashboard.read">
                    <DashboardPage />
                  </RequirePermission>
                }
              />
              <Route
                path="cra-check"
                element={
                  <RequirePermission permission="dashboard.read">
                    <CraCheckPage />
                  </RequirePermission>
                }
              />
              <Route
                path="schwachstellen"
                element={
                  <RequirePermission permission="dt.read">
                    <SchwachstellenPage />
                  </RequirePermission>
                }
              />
              <Route
                path="triage"
                element={
                  <RequirePermission permission="triage.read">
                    <TriagePage />
                  </RequirePermission>
                }
              />
              <Route
                path="audit-log"
                element={
                  <RequirePermission permission="triage.read">
                    <AuditLogPage />
                  </RequirePermission>
                }
              />
              <Route
                path="verantwortlichkeit"
                element={
                  <RequirePermission permission="cmdb.read">
                    <VerantwortlichkeitPage />
                  </RequirePermission>
                }
              />
              <Route
                path="stammdaten"
                element={
                  <RequirePermission permission="cmdb.read">
                    <StammdatenPage />
                  </RequirePermission>
                }
              />
              <Route
                path="beziehungen"
                element={
                  <RequirePermission permission="cmdb.read">
                    <BeziehungenPage />
                  </RequirePermission>
                }
              />
              <Route
                path="import"
                element={
                  <RequirePermission permission="import.write">
                    <ImportPage />
                  </RequirePermission>
                }
              />
              <Route
                path="admin/users"
                element={
                  <RequirePermission permission="user.admin">
                    <AdminUsersPage />
                  </RequirePermission>
                }
              />
              <Route
                path="admin/system"
                element={
                  <RequirePermission permission="system.admin">
                    <SystemPage />
                  </RequirePermission>
                }
              >
                <Route index element={<SystemOverviewPage />} />
                <Route path="csaf" element={<SystemCsafPage />} />
                <Route path="sbom" element={<SystemSbomPage />} />
                <Route path="datenmodell" element={<SystemDataModelPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </ToastProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

// Keep one router instance across renders (including StrictMode). The wildcard
// preserves descendant route matching and keeps providers mounted on navigation.
const router = createBrowserRouter([{ path: '*', element: <ApplicationRoutes /> }]);

export function App(): ReactNode {
  return <RouterProvider router={router} />;
}

export default App;
